import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Slide,
    MenuItem,
    InputBase,
    TextField,
    Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { logout, updateProfile } from '../../../Services/authService';
import { useThemeContext } from '../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import SuccessNotification from '../../../Components/Common/SuccessNotification';
import LoadingAnimation from '../../../Components/Common/LoadingAnimation';
import { LogoutIcon } from '../CustomIcons';
import AvatarSelector from './AvatarSelector';
import { StyledTextField } from '../../Common/StyledTextField';
import { FiberManualRecord } from '@mui/icons-material'
import AccountUpdate from './AccountUpdate';
import UpdatePassword from './UpdatePassword';
import countryCodes from '../full_country_calling_codes.json';
import useAuth from '../../../Hooks/useAuth';
import "./Navbar.css";

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function Profile({ open, onClose, profileData, fetchCurrentUser }) {
    const { t, i18n } = useTranslation();
    const { mode } = useThemeContext();
    const { user } = useAuth();

    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const modalBackgrounColor = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const btnColor1 = mode === 'dark' ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
    const borderColor2 = mode === 'dark' ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;

    const [activeTab, setActiveTab] = useState(0)
    const [formData, setFormData] = useState({});
    const [selectedAvatar, setSelectedAvatar] = useState("");
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successNotification, setSuccessNotification] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [language, setLanguage] = useState(i18n.language);
    const [countryCode, setCountryCode] = useState({ name: "Germany", dial_code: "+49" });
    const [mobileNumber, setMobileNumber] = useState('');

    const handleLogout = async () => {
        try {
            logout();
        } catch (error) {
            console.error(t("navbar.logoutFailed"), error);
        }
    };

    useEffect(() => {
        const fullMobile = profileData?.mobile || '';
        const matched = countryCodes.find(c => fullMobile.startsWith(c.dial_code));
        if (matched) {
            setCountryCode(matched);
            setMobileNumber(fullMobile.replace(matched.dial_code, '').trim());
        } else {
            setMobileNumber(fullMobile);
        }
        setFormData({
            id: profileData?.id,
            login: profileData?.login,
            firstName: profileData?.firstName,
            lastName: profileData?.lastName,
            email: profileData?.email,
            mobile: profileData?.mobile
        })
        setSelectedAvatar(profileData?.profilePictureUrl || "");
    }, [profileData, open]);

    // Sync language state with i18n language changes
    useEffect(() => {
        setLanguage(i18n.language);
    }, [i18n.language]);

    const handleClose = () => {
        setSelectedAvatar(profileData?.profilePictureUrl || "");
        setImageFile(null);
        setErrors({});
        setGeneralError('');
        onClose();
        setTimeout(() => {
            setActiveTab(0)
        }, 1000);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.firstName) newErrors.firstName = t('navbar.firstNameRequired');
        if (!formData.lastName) newErrors.lastName = t('membershipCards.Lastnamerequired');
        if (!formData.login) newErrors.login = t('navbar.usernameRequired');
        if (!mobileNumber) newErrors.mobile = t('membershipCards.Mobilenumberrequired');
        else if (!/^\d{5,15}$/.test(mobileNumber)) newErrors.mobile = t('membershipCards.phoneNumberMustBe5to15');
        if (!formData.email) {
            newErrors.email = t('navbar.emailRequired');
        } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
            newErrors.email = t('navbar.invalidEmailFormat');
        }

        setErrors(newErrors);
        setGeneralError(
            Object.keys(newErrors)?.length > 0
                ? (newErrors.mobile || t('nav.fillAllRequiredFields'))
                : ''
        );
        return Object.keys(newErrors)?.length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        try {
            setLoading(true)
            formData.mobile = `${countryCode.dial_code} ${mobileNumber}`.trim();

            // Create FormData object
            const formDataToSend = new FormData();
            formDataToSend.append("user", JSON.stringify(formData));
            if (imageFile) {
                formDataToSend.append("file", imageFile);
            } else if (selectedAvatar && selectedAvatar !== profileData?.profilePictureUrl) {
                const response = await fetch(selectedAvatar);
                const blob = await response.blob();
                const file = new File([blob], "profile-avatar.png", { type: blob.type });
                formDataToSend.append("profilePicture", file);
            }

            await updateProfile(formDataToSend);

            setSuccessNotification(true); // Show success notification
            fetchCurrentUser()
            onClose(); // Close the dialog
        } catch (error) {
            // setGeneralError(error?.response?.data?.detail);
            const apiMessage = error?.response?.data?.message;
            if (apiMessage === "error.emailexists") {
                setGeneralError(t("users.emailAlreadyExists"));
            } else if (apiMessage === "error.phoneNumberExist") {
                setGeneralError(t("users.phoneNumberAlreadyExists"));
            } else if (apiMessage === "error.userexists") {
                setGeneralError(t("users.usernameAlreadyExists"));
            } else {
                setGeneralError(t("users.errorMessage"));
            }
            console.error(t('navbar.errorUpdatingUser'), error);
        } finally {
            setLoading(false)
        }
    };

    const handleLanguageChange = (event) => {
        const newLanguage = event.target.value;
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);

        // Save language to localStorage
        localStorage.setItem('language', newLanguage);
    };

    return (
        <>
            {loading && <LoadingAnimation />}
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Transition}
                PaperProps={{
                    sx: {
                        position: 'absolute',
                        right: 10,
                        top: 30,
                        margin: 0,
                        borderRadius: 3,
                        background: modalBackgrounColor,
                        width: "97%",
                        "@media (max-width: 427px)": {
                            right: 5,
                        },
                    },
                }}
            >
                {/* Title with logoout */}
                <DialogTitle className='profile-sec' sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: {
                        xs: 2,  // base padding
                        sm: 3,  // small screens
                        md: "30px 30px ",
                    }
                }}>
                    <div className='tabs' style={{ marginTop: "10px" }}>
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setActiveTab(0)} className='franchise-heading-text' style={activeTab === 0 ? { color: btnColor, cursor: "pointer" } : { color: borderColor2, cursor: "pointer", fontSize: "18px" }}>{t('navbar.Profile')} </div>
                            {activeTab === 0 && <FiberManualRecord className='heading-dot' sx={{ color: btnColor, display: 'flex', justifySelf: 'center', fontSize: '10px', marginTop: "10px" }}>.</FiberManualRecord>}
                        </div>
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setActiveTab(2)} className='franchise-heading-text' style={activeTab === 2 ? { color: btnColor, cursor: "pointer" } : { color: borderColor2, cursor: "pointer", fontSize: "18px" }}>{t('navbar.Password')}</div>
                            {activeTab === 2 && <FiberManualRecord className='heading-dot' sx={{ color: btnColor, display: 'flex', justifySelf: 'center', fontSize: '10px', marginTop: "10px" }}>.</FiberManualRecord>}
                        </div>
                        {user?.roles?.includes('ROLE_ADMIN') && (
                            <div style={{ position: 'relative' }}>
                                <div onClick={() => setActiveTab(1)} className='franchise-heading-text' style={activeTab === 1 ? { color: btnColor, cursor: "pointer" } : { color: borderColor2, cursor: "pointer", fontSize: "18px" }}>{t('navbar.Account')}</div>
                                {activeTab === 1 && <FiberManualRecord className='heading-dot' sx={{ color: btnColor, display: 'flex', justifySelf: 'center', fontSize: '10px', marginTop: "10px" }}>.</FiberManualRecord>}
                            </div>
                        )}
                    </div>
                    <IconButton className='add-machine logout-btn ' onClick={handleLogout}
                        sx={{
                            display: 'flex',
                            borderRadius: '40px', padding: '12px 26px 13px', backgroundColor: btnColor1, color: "#fff", mr: '7px', ml: '16px',
                            "&:hover": {
                                backgroundColor: btnColor1,
                                color: "#fff",
                                boxShadow: "none",
                            },
                        }} >
                        {/* <Typography className='add-machine ' variant="subtitle1"> {t('loginPage.logout')}</Typography> */}

                        {t('loginPage.logout')}
                        <LogoutIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{
                    overflowY: 'auto', paddingTop: 1,
                    p: {
                        xs: 2,  // base padding
                        sm: 3,  // small screens
                        md: "30px 30px ",
                    }
                }}>
                    {activeTab === 0 &&
                        <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <AvatarSelector
                                selectedAvatar={selectedAvatar}
                                onAvatarSelect={setSelectedAvatar}
                                onFileSelect={setImageFile} />
                            <Typography variant="subtitle1">{t('loginPage.general')}</Typography>
                            {/* FirstName */}
                            <Box>
                                <StyledTextField
                                    label={`${t('loginPage.firstname')}`}
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    error={errors.firstName}
                                    inputBoxColor={inputBoxColor}
                                    required
                                />
                            </Box>
                            {/* LastName */}
                            <Box>
                                <StyledTextField
                                    label={`${t('loginPage.lastname')}`}
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    error={errors.lastName}
                                    inputBoxColor={inputBoxColor}
                                    required
                                />
                            </Box>
                            {/* Username */}
                            <Box>
                                <StyledTextField
                                    label={`${t('loginPage.username')}`}
                                    name="login"
                                    value={formData.login}
                                    onChange={handleChange}
                                    error={errors.login}
                                    inputBoxColor={inputBoxColor}
                                    required
                                    disabled={true}
                                />
                            </Box>
                            {/* Email */}
                            <Box>
                                <StyledTextField
                                    label={`${t('loginPage.email')}`}
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={errors.email}
                                    inputBoxColor={inputBoxColor}
                                    required
                                />
                            </Box>
                            {/* phonennumber */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    backgroundColor: inputBoxColor,
                                    height: '50px',
                                }}
                            >
                                {/* Country Code Dropdown */}
                                <Autocomplete
                                    options={countryCodes}
                                    getOptionLabel={(option) => `${option.name} (${option.dial_code})`}
                                    value={countryCode}
                                    onChange={(e, newValue) => {
                                        if (newValue) setCountryCode(newValue);
                                    }}
                                    isOptionEqualToValue={(option, value) => option.dial_code === value.dial_code}
                                    sx={{
                                        minWidth: 185,
                                        '& .MuiInputBase-root': {
                                            height: '48px',
                                            borderRight: `1px solid ${borderColor2}`,
                                            borderRadius: 0,
                                            backgroundColor: 'transparent',
                                            px: 2,
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            InputProps={{ ...params.InputProps, disableUnderline: true,
                                                style: {
                                                  color: errors.mobile ? 'red' : 'inherit',
                                                }
                                            }}
                                        />
                                    )}
                                />

                                {/* Phone Number Input */}
                                <InputBase
                                    placeholder={`${t('membershipCards.mobileNumber')} *`}
                                    value={mobileNumber}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                        setMobileNumber(digitsOnly);
                                        if (errors.mobile) {
                                            setErrors((prev) => ({ ...prev, mobile: '' }));
                                            setGeneralError('');
                                        }
                                    }}
                                    inputProps={{
                                        style: {
                                            color: errors.mobile ? 'red' : 'inherit',
                                        }
                                    }}
                                    sx={{ px: 2, flex: 1, height: '100%' }}
                                />
                            </Box>
                            {/* <Box>
                                <StyledTextField
                                    label={`${t('loginPage.phoneNumber')}`}
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    error={errors.mobile}
                                    inputBoxColor={inputBoxColor}
                                    required
                                />
                            </Box> */}
                            <Typography variant="subtitle1">{t('loginPage.settings')}</Typography>
                            {/* Language */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <StyledTextField
                                    id="language-select"
                                    label={`${t('loginPage.language')}`}
                                    name="language"
                                    value={language}
                                    onChange={handleLanguageChange}
                                    error={errors.language}
                                    inputBoxColor={inputBoxColor}
                                >
                                    <MenuItem value="de">German</MenuItem>
                                    <MenuItem value="en">English</MenuItem>
                                    <MenuItem value="fr">French</MenuItem>
                                    <MenuItem value="es">Spanish</MenuItem>
                                    <MenuItem value="it">Italian</MenuItem>
                                    <MenuItem value="ch">Chinese</MenuItem>
                                </StyledTextField>
                            </Box>

                            {generalError && (
                                <Typography color="error" variant="body2" sx={{ textAlign: "center", marginTop: 1 }}>
                                    {generalError}
                                </Typography>
                            )}
                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <button type="button" style={{ color: btnColor, borderColor: btnColor }} className='cancel-btn' onClick={handleClose}>{t('membershipCards.cancel')}</button>
                                <button type="button" style={{ background: btnColor }} className='save-btn' onClick={handleSave}>{t('membershipCards.save')}</button>
                            </Box>
                        </Box>
                    }
                    {activeTab === 1 &&
                        <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <AccountUpdate handleClose={handleClose} onSuccess={() => { setSuccessNotification(true); handleClose(); }} />
                        </Box>
                    }
                    {activeTab === 2 &&
                        <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <UpdatePassword handleClose={handleClose} onSuccess={() => { setSuccessNotification(true); handleClose(); }} />
                        </Box>
                    }
                </DialogContent>
            </Dialog >

            {/* Use Notification Component */}
            <SuccessNotification
                open={successNotification}
                message={t('membershipCards.successMessage')}
                severity="success"
                onClose={() => setSuccessNotification(false)
                }
            />
        </>
    );
}

export default Profile;
